import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { controlErrorClass, fieldAnatomyStyles, fieldMessage, nextFieldMessageId, renderFieldLabel, renderFieldMessage } from './field-anatomy.js';
import { fieldHelpDockActiveContext } from './field-help-dock-context.js';

export interface CosRadioOption {
	value: string;
	label: string;
	helpText?: string;
	disabled?: boolean;
}

let _nextId = 1;

/**
 * <cos-radio-group> — the house one-of-N radio set.
 *
 * Each option is a real `<input type="radio">` (visually hidden, wrapped
 * in its own `<label>` for implicit association) sharing one `name`, so
 * native radio semantics — grouping, mutual exclusion — come for free.
 * On top of that, the ARIA APG roving-tabindex pattern: only the
 * selected option (or the first enabled one, if nothing is selected) is
 * in the Tab order; the arrow keys move focus AND selection between
 * options, wrapping at the ends, skipping disabled ones.
 *
 * Props:
 *   - options: { value, label, helpText?, disabled? }[]
 *   - value: string — controlled
 *   - label?: string — a VISIBLE caption above the group (Stage:
 *     fleet/forms-radio-parity flipped this from aria-only to visible,
 *     matching cos-input/cos-select/cos-multiselect — a form-control
 *     primitive's own label placement, never an outside `<label>`/heading,
 *     per component-vocabulary.md's "Form anatomy"). Also wired as the
 *     group's accessible name via `aria-labelledby` (there's no native
 *     `<legend>`-equivalent to inherit one from) regardless of visibility.
 *   - hideLabel?: boolean (`hide-label`, Stage: fleet/forms-radio-parity) —
 *     the cos-input/cos-select pattern: `label` stays the accessible name
 *     (via aria-labelledby, which doesn't depend on visibility) while the
 *     visible caption clips off-screen — for a group with its own heading
 *     rendered elsewhere.
 *   - helpText?: string (Stage: fleet/forms-primitives) — group-level help
 *     text, rendered below the option list
 *   - error?: string (Stage: fleet/forms-primitives) — replaces helpText
 *     with error styling + message, marks the group aria-invalid
 *   - required?: boolean (Stage: fleet/forms-primitives) — renders the `*`
 *     marker next to the visible label (when set) and marks the group
 *     aria-required
 *   - name?: string (Stage: fleet/forms-primitives) — overrides the
 *     per-instance generated `name` shared by the option inputs, for a
 *     consumer that needs a stable/predictable name
 *   - disabled?: boolean — disables every option
 *   - orientation: vertical|horizontal (default vertical)
 *   - renderOption?: (opt: CosRadioOption) => unknown — the
 *     cos-dashboard-grid `renderTile` / cos-tabs `renderPanel` callback
 *     idiom. When set, an option's body is the callback's return value
 *     in place of the plain label+helpText spans — the dot, native
 *     input, keyboard model and checked/disabled states are unchanged.
 *     The callback's template renders INSIDE this component's shadow
 *     root (the same grid-shadow trap as renderTile), so a caller
 *     styling box properties (background/border/padding) must do it
 *     with inline `var()` styles or a traveling `<style>` block
 *     included in the returned template — not via the caller's own
 *     `static styles`, which cannot reach here. A `data-rich` attribute
 *     is set on the option row for that traveling style to hook into
 *     (e.g. `.opt[data-rich]:has(.native:checked)`).
 *
 * Emits `cos:change` (detail: { value }) on pick.
 */
@customElement('cos-radio-group')
export class CosRadioGroup extends LitElement {
	@property({ attribute: false }) options: CosRadioOption[] = [];
	@property({ type: String }) value = '';
	@property({ type: String }) label = '';
	@property({ type: Boolean, attribute: 'hide-label' }) hideLabel = false;
	@property({ type: String }) helpText = '';
	@property({ type: String }) error = '';
	@property({ type: Boolean }) required = false;
	@property({ type: String }) name = '';
	@property({ type: Boolean, reflect: true }) disabled = false;
	@property({ type: String }) orientation: 'vertical' | 'horizontal' = 'vertical';
	@property({ attribute: false }) renderOption?: (opt: CosRadioOption) => unknown;

	// Per-instance, even though a shadow root already scopes native radio
	// grouping to its own tree — belt-and-suspenders against relying on
	// that scoping behaving identically everywhere. Overridable via `name`.
	private readonly _generatedName = `cos-radio-group-${_nextId++}`;
	@consume({ context: fieldHelpDockActiveContext, subscribe: true })
	@state()
	private _dockActive = false;

	private readonly _msgId = nextFieldMessageId('cos-radio-group');
	// aria-labelledby target for the visible caption span, mirroring
	// cos-multiselect's non-dropdown `role="group"` — a radiogroup has no
	// native `<legend>`-equivalent descendant to inherit a name from, so the
	// group is wired to this id rather than a plain aria-label (which the
	// select/multiselect trigger BUTTON uses instead, since a button's own
	// text content would otherwise clash with an aria-label mismatch rule
	// that doesn't apply to a non-interactive `role="radiogroup"` div).
	private readonly _labelId = nextFieldMessageId('cos-radio-group-label');

	private get _groupName(): string {
		return this.name || this._generatedName;
	}

	private get _enabledIndexes(): number[] {
		return this.options
			.map((o, i) => i)
			.filter((i) => !this.disabled && !this.options[i].disabled);
	}

	private _tabIndexFor(i: number): number {
		if (this.disabled || this.options[i].disabled) return -1;
		const selectedIdx = this.options.findIndex((o) => o.value === this.value);
		// A selected value pointing at a disabled option (stale prop state,
		// or an option disabled after being picked) must not leave the
		// group with nothing in the tab order — fall back to the first
		// enabled option, same as the nothing-selected case.
		const selectedIsEnabled = selectedIdx >= 0 && !this.options[selectedIdx].disabled;
		const target = selectedIsEnabled ? selectedIdx : this._enabledIndexes[0];
		return i === target ? 0 : -1;
	}

	private _pick(i: number, focus: boolean): void {
		const opt = this.options[i];
		if (!opt || this.disabled || opt.disabled) return;
		if (focus) {
			void this.updateComplete.then(() => {
				(this.renderRoot.querySelectorAll('input')[i] as HTMLInputElement | undefined)?.focus();
			});
		}
		if (opt.value === this.value) return;
		this.value = opt.value;
		this.dispatchEvent(
			new CustomEvent('cos:change', { detail: { value: opt.value }, bubbles: true, composed: true }),
		);
	}

	private _move(fromIndex: number, delta: number): void {
		const enabled = this._enabledIndexes;
		if (enabled.length === 0) return;
		const pos = enabled.indexOf(fromIndex);
		const nextPos = pos < 0 ? 0 : (pos + delta + enabled.length) % enabled.length;
		this._pick(enabled[nextPos], true);
	}

	private _onKeydown(e: KeyboardEvent, i: number): void {
		switch (e.key) {
			case 'ArrowDown':
			case 'ArrowRight':
				e.preventDefault();
				this._move(i, 1);
				break;
			case 'ArrowUp':
			case 'ArrowLeft':
				e.preventDefault();
				this._move(i, -1);
				break;
		}
	}

	static styles = css`
		${fieldAnatomyStyles}
		:host {
			display: block;
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-primary);
		}
		/* Stage: fleet/forms-radio-parity — :host is already block, so the
		   group div starts a new line regardless; these just give the label
		   row and the message row their own vertical rhythm, matching the
		   cos-select/cos-multiselect convention. */
		.label-text {
			display: block;
			margin-bottom: var(--cos-space-2);
		}
		.msg {
			display: block;
			margin-top: var(--cos-space-2);
		}
		.group {
			display: flex;
			gap: var(--cos-space-2);
		}
		.group[data-orientation='vertical'] {
			flex-direction: column;
		}
		.group[data-orientation='horizontal'] {
			flex-direction: row;
			flex-wrap: wrap;
			gap: var(--cos-space-4);
		}
		.opt {
			display: inline-flex;
			align-items: flex-start;
			gap: var(--cos-space-2);
			cursor: pointer;
		}
		.opt[data-disabled] {
			cursor: not-allowed;
			opacity: 0.6;
		}
		.native {
			position: absolute;
			width: 1px;
			height: 1px;
			opacity: 0;
			margin: 0;
			pointer-events: none;
		}
		.dot {
			flex: none;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 18px;
			height: 18px;
			margin-top: 1px;
			box-sizing: border-box;
			border: 1.5px solid var(--cos-border);
			border-radius: var(--cos-radius-pill);
			background: var(--cos-surface);
			transition: background-color var(--cos-transition-fast), border-color var(--cos-transition-fast);
		}
		.dot::after {
			content: '';
			width: 9px;
			height: 9px;
			border-radius: var(--cos-radius-pill);
			background: var(--cos-text-on-color);
			opacity: 0;
		}
		.native:checked + .dot {
			border-color: var(--cos-brand-500);
			background: var(--cos-brand-500);
		}
		.native:checked + .dot::after {
			opacity: 1;
		}
		.native:focus-visible + .dot {
			outline: none;
			box-shadow: var(--cos-shadow-focus);
		}
		.text {
			display: flex;
			flex-direction: column;
			gap: 2px;
		}
		.text.rich {
			flex: 1;
			min-width: 0;
		}
		.help {
			font-size: var(--cos-font-size-xs);
			color: var(--cos-text-secondary);
		}
	`;

	render() {
		const { message, isError } = fieldMessage(this.helpText, this.error);
		const dotClass = `dot${controlErrorClass(!!this.error)}`;
		return html`
			${renderFieldLabel({ label: this.label, required: this.required, hideLabel: this.hideLabel, id: this.label ? this._labelId : undefined })}
			<div
				class="group"
				role="radiogroup"
				aria-labelledby=${this.label ? this._labelId : nothing}
				aria-orientation=${this.orientation}
				aria-invalid=${this.error ? 'true' : nothing}
				aria-required=${this.required ? 'true' : nothing}
				aria-describedby=${message ? this._msgId : nothing}
				data-orientation=${this.orientation}
			>
				${this.options.map((o, i) => {
					const isDisabled = this.disabled || !!o.disabled;
					return html`
						<label class="opt" ?data-disabled=${isDisabled} ?data-rich=${!!this.renderOption}>
							<input
								class="native"
								type="radio"
								name=${this._groupName}
								.checked=${o.value === this.value}
								?disabled=${isDisabled}
								tabindex=${this._tabIndexFor(i)}
								@change=${() => this._pick(i, false)}
								@keydown=${(e: KeyboardEvent) => this._onKeydown(e, i)}
							/>
							<span class=${dotClass} aria-hidden="true"></span>
							${this.renderOption
								? html`<span class="text rich">${this.renderOption(o)}</span>`
								: html`<span class="text">
										<span>${o.label}</span>
										${o.helpText ? html`<span class="help">${o.helpText}</span>` : ''}
									</span>`}
						</label>
					`;
				})}
			</div>
			${renderFieldMessage({ message, isError, id: message ? this._msgId : undefined, visuallyHidden: this._dockActive && !isError })}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-radio-group': CosRadioGroup;
	}
}
